// File: src/lib/goalbuddyTaskRunStore.ts
//
// GoalBuddy TaskRun Store (v1)
// ----------------------------
// Canonical API used by:
// - src/pages/goalbuddy/GoalBuddyTaskRunner.tsx
// - src/pages/goalbuddy/GoalBuddySessionsPanel.tsx
//
// Phase GB-R1 goals:
// - Single canonical module (no duplicate stores)
// - ESM-safe named exports for all call sites
// - Local deterministic persistence (localStorage) until Firestore wiring lands
//
// IMPORTANT:
// - This module is "library-grade": no React imports, no component state fabrication.
// - All writes emit a local event so subscribers update immediately.
// - ✅ Additionally hydrates OmniCine AtomicRuns via bridge emitter (no double-dispatch):
//     emitAtomicRunsUpdate({ sessionId, runs, source, note })

import { emitAtomicRunsUpdate } from "@/bridge/goalbuddy/emitAtomicRunsUpdate";

export type CauseCategory =
  | "energy_depletion"
  | "cognitive_overload"
  | "emotional_spike"
  | "decision_fog"
  | "attention_fragmentation"
  | "social_friction";

export type TaskType = "recover" | "stabilize" | "clarify" | "commit" | "communicate";

export type TaskRunStatus = "draft" | "active" | "paused" | "completed" | "abandoned";

export type TaskRunNotesV1 = {
  circledItem?: string;
  boundary?: string;
  nextAction?: string;
  scratchBullets?: string[];
};

export type TaskRunV1 = {
  id: string;
  uid: string;
  sid: string;

  taskId: string;
  title: string;
  type: TaskType;
  cause: CauseCategory;

  status: TaskRunStatus;

  // Timestamps can be numbers (epoch ms). UI already supports Firestore-like shapes too.
  createdAt: number;
  updatedAt: number;

  // Runner artifacts
  intensityBefore?: number;
  intensityAfter?: number;

  steps?: Array<{ label: string; done?: boolean }>;
  checks?: boolean[];

  notes?: TaskRunNotesV1;
};

type SubscribeOpts = { limit?: number };

const STORE_PREFIX = "goalbuddy.taskRuns.v1";
const LOCAL_EVENT = "goalbuddy:taskRuns:changed";

/**
 * Provenance for OmniCine reducer meta.source.
 * Matches TimelineSource union: "user" | "ai-assist" | "system" | "xr-user" | "console"
 */
export type AtomicRunSource = "user" | "ai-assist" | "system" | "xr-user" | "console";

/**
 * OmniCine AtomicRun (v1 projection)
 * Note: Timeline types define this shape; we keep this store dependency-free.
 */
export type AtomicRunV1 = {
  id: string;
  startSec: number;
  durSec: number;
  label: string;
  status?: "planned" | "active" | "done" | "skipped";
  tags?: string[];
  ownerId?: string;
  sessionId?: string;
  notes?: { scratchBullets?: string[]; scratchText?: string };
  source?: AtomicRunSource;
  updatedAt?: number;
  v?: 1;
};

function now(): number {
  return Date.now();
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function keyFor(uid: string, sid: string): string {
  return `${STORE_PREFIX}:${uid}:${sid}`;
}

function readMap(uid: string, sid: string): Record<string, TaskRunV1> {
  const raw = safeParse<Record<string, TaskRunV1>>(localStorage.getItem(keyFor(uid, sid)));
  if (!raw || typeof raw !== "object") return Object.create(null);

  // Normalize to a plain map
  const out: Record<string, TaskRunV1> = Object.create(null);
  for (const [id, v] of Object.entries(raw)) {
    if (!v || typeof v !== "object") continue;
    if (typeof (v as any).id !== "string") continue;
    out[id] = v as TaskRunV1;
  }
  return out;
}

function listRuns(uid: string, sid: string): TaskRunV1[] {
  const map = readMap(uid, sid);
  return Object.values(map).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/* ------------------------------------------------------------------ */
/* AtomicRuns projection (store owns projection; bridge owns emission)  */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function finiteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function mapStatus(s: TaskRunStatus): AtomicRunV1["status"] {
  if (s === "active") return "active";
  if (s === "paused") return "planned";
  if (s === "completed") return "done";
  if (s === "abandoned") return "skipped";
  return "planned";
}

/**
 * Deterministic projection rule (local-only; stable):
 * - anchorMs = min(createdAt) across session runs (or now if none)
 * - startSec = (createdAt - anchorMs) / 1000
 * - durSec   = max(0.05, (updatedAt - createdAt) / 1000)
 *
 * This gives a consistent lane visualization without fabricating wall-clock-to-timeline coupling.
 * When you introduce a real timeline clock for GoalBuddy, you can replace this mapping.
 */
function taskRunsToAtomicRuns(uid: string, sid: string, rows: TaskRunV1[], source: AtomicRunSource): AtomicRunV1[] {
  const safeRows = Array.isArray(rows) ? rows : [];
  const anchorMs =
    safeRows.length > 0
      ? safeRows.reduce((min, r) => {
          const t = finiteNum((r as any)?.createdAt) ? Number((r as any).createdAt) : now();
          return Math.min(min, t);
        }, Number.POSITIVE_INFINITY)
      : now();

  // Timeline wants increasing time; listRuns is desc by updatedAt, so re-sort by createdAt asc for projection.
  const byCreatedAsc = safeRows
    .slice()
    .sort((a, b) => (Number(a.createdAt || 0) || 0) - (Number(b.createdAt || 0) || 0));

  return byCreatedAsc.map((r) => {
    const createdAt = finiteNum(r.createdAt) ? Number(r.createdAt) : anchorMs;
    const updatedAt = finiteNum(r.updatedAt) ? Number(r.updatedAt) : createdAt;

    const startSec = clamp((createdAt - anchorMs) / 1000, 0, Number.MAX_SAFE_INTEGER);
    const durSec = Math.max(0.05, (updatedAt - createdAt) / 1000);

    const scratchBullets = Array.isArray(r?.notes?.scratchBullets) ? r.notes!.scratchBullets : undefined;

    const scratchTextParts: string[] = [];
    if (typeof r?.notes?.circledItem === "string" && r.notes.circledItem.trim())
      scratchTextParts.push(`circled: ${r.notes.circledItem.trim()}`);
    if (typeof r?.notes?.boundary === "string" && r.notes.boundary.trim())
      scratchTextParts.push(`boundary: ${r.notes.boundary.trim()}`);
    if (typeof r?.notes?.nextAction === "string" && r.notes.nextAction.trim())
      scratchTextParts.push(`next: ${r.notes.nextAction.trim()}`);

    const scratchText = scratchTextParts.length ? scratchTextParts.join("\n") : undefined;

    return {
      id: String(r.id),
      startSec,
      durSec,
      label: String(r.title || `Run ${r.id}`),
      status: mapStatus(r.status),
      tags: [r.type, r.cause].filter(Boolean) as string[],
      ownerId: uid,
      sessionId: sid,
      notes: scratchBullets || scratchText ? { scratchBullets, scratchText } : undefined,
      source,
      updatedAt: now(),
      v: 1,
    } satisfies AtomicRunV1;
  });
}

/* ------------------------------------------------------------------ */
/* Persistence write boundary                                          */
/* ------------------------------------------------------------------ */

function writeMap(uid: string, sid: string, map: Record<string, TaskRunV1>, source: AtomicRunSource = "user"): void {
  localStorage.setItem(keyFor(uid, sid), JSON.stringify(map));

  // Local store subscribers
  try {
    window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: { uid, sid } }));
  } catch {
    // ignore
  }

  // ✅ OmniCine hydration (atomicRuns/setAll) — via bridge emitter (no local CustomEvent here)
  try {
    const rows = Object.values(map || {});
    const runs = taskRunsToAtomicRuns(uid, sid, rows, source);

    emitAtomicRunsUpdate({
      sessionId: sid,
      runs,
      source,
      note: "goalbuddyTaskRunStore->atomicRuns:update",
    });
  } catch {
    // ignore
  }
}

export function makeRunId(): string {
  // deterministic-ish: time + random tail
  // e.g. gb_1707040000000_k3m9p2
  return `gb_${String(now())}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * subscribeTaskRuns(uid, sid, onRows, {limit})
 * -------------------------------------------
 * Live updates:
 * - immediate callback (current snapshot)
 * - updates when:
 *   - local writes happen (LOCAL_EVENT)
 *   - another tab writes to localStorage (storage event)
 */
export function subscribeTaskRuns(
  uid: string,
  sid: string,
  onRows: (rows: TaskRunV1[]) => void,
  opts?: SubscribeOpts,
): () => void {
  const limit = opts?.limit ?? 50;

  const emit = () => {
    const rows = listRuns(uid, sid).slice(0, limit);
    onRows(rows);
  };

  emit();

  const onLocal = (e: Event) => {
    const ce = e as CustomEvent<any>;
    if (!ce?.detail) return;
    if (ce.detail.uid === uid && ce.detail.sid === sid) emit();
  };

  const onStorage = (e: StorageEvent) => {
    if (e.key === keyFor(uid, sid)) emit();
  };

  window.addEventListener(LOCAL_EVENT, onLocal as any);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(LOCAL_EVENT, onLocal as any);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * upsertTaskRunsFromService(uid, sid, rows, source?)
 * -------------------------------------------------
 * Use this for non-user provenance:
 * - "system": hydration / sync
 * - "ai-assist": model-generated runs
 *
 * NOTE: rows are keyed by id; this replaces/merges deterministically.
 */
export async function upsertTaskRunsFromService(
  uid: string,
  sid: string,
  rows: TaskRunV1[],
  source: AtomicRunSource = "system",
): Promise<void> {
  const map = readMap(uid, sid);

  const list = Array.isArray(rows) ? rows : [];
  for (const r of list) {
    if (!r || typeof r !== "object") continue;
    if (typeof (r as any).id !== "string" || !(r as any).id.trim()) continue;

    const id = String((r as any).id);
    map[id] = {
      ...(map[id] ?? {}),
      ...(r as TaskRunV1),
      id,
      uid,
      sid,
      createdAt: finiteNum((r as any).createdAt) ? Number((r as any).createdAt) : now(),
      updatedAt: now(),
    };
  }

  writeMap(uid, sid, map, source);
}

/**
 * patchTaskRun(uid, sid, runId, patch)
 * -----------------------------------
 * Shallow patch of an existing run.
 * Throws if run is missing (by design) — caller should use startTaskRun to create.
 *
 * Patch 1 (recommended):
 * - Deep-merge notes so partial patches do not wipe other note fields.
 */
export async function patchTaskRun(
  uid: string,
  sid: string,
  runId: string,
  patch: Partial<Omit<TaskRunV1, "id" | "uid" | "sid" | "createdAt">>,
): Promise<TaskRunV1> {
  const map = readMap(uid, sid);
  const existing = map[runId];

  if (!existing) {
    throw new Error(`[goalbuddyTaskRunStore] patchTaskRun: missing run "${runId}" for uid="${uid}" sid="${sid}"`);
  }

  const nextNotes =
    patch.notes !== undefined
      ? {
          ...(existing.notes || {}),
          ...(patch.notes || {}),
          // if scratchBullets provided, use it; otherwise preserve existing list
          scratchBullets:
            (patch.notes as any)?.scratchBullets !== undefined
              ? (patch.notes as any).scratchBullets
              : existing.notes?.scratchBullets,
        }
      : existing.notes;

  const updated: TaskRunV1 = {
    ...existing,
    ...patch,
    id: existing.id,
    uid: existing.uid,
    sid: existing.sid,
    createdAt: existing.createdAt,
    updatedAt: now(),
    notes: nextNotes,
  };

  map[runId] = updated;
  writeMap(uid, sid, map, "user");
  return updated;
}

export async function setTaskRunStatus(
  uid: string,
  sid: string,
  runId: string,
  status: TaskRunStatus,
): Promise<TaskRunV1> {
  return patchTaskRun(uid, sid, runId, { status });
}

/**
 * startTaskRun(uid, sid, init)
 * ----------------------------
 * Create a new run (draft by default) unless init.status provided.
 */
export async function startTaskRun(
  uid: string,
  sid: string,
  init: {
    taskId: string;
    title: string;
    type: TaskType;
    cause: CauseCategory;
    status?: TaskRunStatus;
    intensityBefore?: number;
    steps?: Array<{ label: string; done?: boolean }>;
    checks?: boolean[];
    notes?: TaskRunNotesV1;
  },
): Promise<TaskRunV1> {
  const map = readMap(uid, sid);
  const id = makeRunId();
  const t = now();

  const run: TaskRunV1 = {
    id,
    uid,
    sid,
    taskId: init.taskId,
    title: init.title,
    type: init.type,
    cause: init.cause,
    status: init.status ?? "draft",
    createdAt: t,
    updatedAt: t,
    intensityBefore: init.intensityBefore,
    steps: init.steps,
    checks: init.checks,
    notes: init.notes,
  };

  map[id] = run;
  writeMap(uid, sid, map, "user");
  return run;
}

/**
 * updateTaskRun(uid, sid, runId, patch)
 * -------------------------------------
 * Alias to patchTaskRun for runner semantics.
 */
export async function updateTaskRun(
  uid: string,
  sid: string,
  runId: string,
  patch: Partial<Omit<TaskRunV1, "id" | "uid" | "sid" | "createdAt">>,
): Promise<TaskRunV1> {
  return patchTaskRun(uid, sid, runId, patch);
}

export async function pauseTaskRun(uid: string, sid: string, runId: string): Promise<TaskRunV1> {
  return setTaskRunStatus(uid, sid, runId, "paused");
}

export async function abandonTaskRun(uid: string, sid: string, runId: string): Promise<TaskRunV1> {
  return setTaskRunStatus(uid, sid, runId, "abandoned");
}

export async function completeTaskRun(
  uid: string,
  sid: string,
  runId: string,
  opts?: { intensityAfter?: number },
): Promise<TaskRunV1> {
  return patchTaskRun(uid, sid, runId, {
    status: "completed",
    intensityAfter: typeof opts?.intensityAfter === "number" ? opts.intensityAfter : undefined,
  });
}

